import React, { Fragment, useState } from 'react';
import { VaultCreateKybKycSubmitAuditEntry } from '../types';
import VgsShowValue from '../../VgsInput/VgsShowValue';
import LoadingScreen from '../../LoadingScreen';
import ErrorMessage from '../../ErrorMessage';
import useFieldRevealTracker from '../../../hooks/useFieldRevealTracker';
import RawDisplayValue from '../../RawValueDisplay';
import JsonDisplayValue from '../../JsonDisplayValue';
import logger from '../../../util/logger';

export default function CreateKybKycLog({
  log,
  token,
}: {
  log: VaultCreateKybKycSubmitAuditEntry;
  token: string;
}): React.JSX.Element {
  logger(`CreateKybKycLog: ${JSON.stringify(log)}`);
  const [fieldsAreRevealed, setFieldsAreRevealed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const onRevealFail = (): void =>
    setErrorMessage('Could not load data, please try again.');

  const onFieldRevealSuccess = useFieldRevealTracker(
    () => setFieldsAreRevealed(true),
    [
      'kybLegalName',
      'kybCustomerFacingName',
      'kybDescription',
      'kybUrl',
      'kybPhoneNumber',
      'kybEmail',
      'kybTaxId',
      'kybAddressStreet',
      'kybAddressUnit',
      'kybAddressCity',
      'kybAddressRegion',
      'kybAddressPostalCode',
      'representativeFirstName',
      'representativeLastName',
      'representativePhoneNumber',
      'representativeEmail',
      'representativeSsn',
      'representativeDobDay',
      'representativeDobMonth',
      'representativeDobYear',
      'representativeAddressStreet',
      'representativeAddressUnit',
      'representativeAddressCity',
      'representativeAddressRegion',
      'representativeAddressPostalCode',
      // todo: beneficial owners
    ],
  );

  const sharedProps = {
    onRevealFail,
    token,
    type: 'text' as VgsFieldType,
  };

  return (
    <Fragment>
      {!fieldsAreRevealed && <LoadingScreen />}
      {errorMessage ? (
        <ErrorMessage message={errorMessage} showThankYou={false} />
      ) : (
        <div
          className='p-6'
          style={{ visibility: fieldsAreRevealed ? 'visible' : 'hidden' }}
        >
          {/* created */}
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.created.toLocaleString()}
                label='Created'
              />
            </div>
          </div>
          <h2 className='text-xl font-medium mb-4'>Orgs Data</h2>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue value={log.userId} label='User ID' />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue value={log.orgId} label='Org ID' />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.saveToPayableOrgId}
                label='Save to Payable Org ID'
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.financialAccountNodeId}
                label='Financial Account Node ID'
              />
            </div>
          </div>
          <h2 className='text-xl font-medium mb-4'>Stripe Data</h2>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeEnv}
                label='Stripe Environment'
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.statementDescriptor}
                label='Statement Descriptor'
              />
            </div>
          </div>
          {/* stripe metadata 1/2 */}
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeMetadataEntity}
                label='Stripe Metadata Entity'
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeMetadataActivityType}
                label='Stripe Metadata Activity Type'
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeMetadataFundraiserId}
                label='Stripe Metadata Fundraiser ID'
              />
            </div>
          </div>
          {/* stripe metadata 2/2 */}
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeMetadataPaymentType}
                label='Stripe Metadata Payment Type'
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeMetadataTransactionId}
                label='Stripe Metadata Transaction ID'
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.stripeMetadataFundraiserIdLegacy}
                label='Stripe Metadata Fundraiser ID Legacy'
              />
            </div>
          </div>
          <h2 className='text-xl font-medium mb-4'>Kyb Data</h2>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.kybLegalName}
                label='Kyb Legal Name'
                onRevealSuccess={onFieldRevealSuccess('kybLegalName')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.kybCustomerFacingName}
                label='Kyb Customer Facing Name'
                onRevealSuccess={onFieldRevealSuccess('kybCustomerFacingName')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue value={log.kybId} label='Kyb ID' />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.kybRaiseFundraiserId}
                label='Kyb Fundraiser ID'
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.kybDescription}
                label='Kyb Description'
                onRevealSuccess={onFieldRevealSuccess('kybDescription')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.kybTaxId}
                label='Kyb Tax ID'
                onRevealSuccess={onFieldRevealSuccess('kybTaxId')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.kybPhoneNumber}
                label='Kyb Phone Number'
                onRevealSuccess={onFieldRevealSuccess('kybPhoneNumber')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.kybEmail}
                label='Kyb Email'
                onRevealSuccess={onFieldRevealSuccess('kybEmail')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.kybUrl}
                label='Kyb URL'
                onRevealSuccess={onFieldRevealSuccess('kybUrl')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.kybAddressStreet}
                label='Kyb Address Street'
                onRevealSuccess={onFieldRevealSuccess('kybAddressStreet')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.kybAddressUnit}
                label='Kyb Address Unit'
                onRevealSuccess={onFieldRevealSuccess('kybAddressUnit')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.kybAddressCity}
                label='Kyb Address City'
                onRevealSuccess={onFieldRevealSuccess('kybAddressCity')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.kybAddressRegion}
                label='Kyb Address Region'
                onRevealSuccess={onFieldRevealSuccess('kybAddressRegion')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.kybAddressPostalCode}
                label='Kyb Address Postal Code'
                onRevealSuccess={onFieldRevealSuccess('kybAddressPostalCode')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue value={log.kybType} label='Kyb Type' />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue value={log.kybStructure} label='Kyb Structure' />
            </div>
          </div>
          <h2 className='text-xl font-medium mb-4'>Representative Data</h2>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue value={log.representativeKycId} label='Kyc ID' />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.representativeStripePersonId}
                label='Stripe Person ID'
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.representativeFirstName}
                label='Representative First Name'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativeFirstName',
                )}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.representativeLastName}
                label='Representative Last Name'
                onRevealSuccess={onFieldRevealSuccess('representativeLastName')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativePhoneNumber}
                label='Representative Phone Number'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativePhoneNumber',
                )}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeEmail}
                label='Representative Email'
                onRevealSuccess={onFieldRevealSuccess('representativeEmail')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeSsn}
                label='Representative SSN'
                onRevealSuccess={onFieldRevealSuccess('representativeSsn')}
                replacePatterns={[['\\d', 'X', 5]]}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeDobDay}
                label='Representative DOB Day'
                onRevealSuccess={onFieldRevealSuccess('representativeDobDay')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeDobMonth}
                label='Representative DOB Month'
                onRevealSuccess={onFieldRevealSuccess('representativeDobMonth')}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeDobYear}
                label='Representative DOB Year'
                onRevealSuccess={onFieldRevealSuccess('representativeDobYear')}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.representativeAddressStreet}
                label='Representative Address Street'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativeAddressStreet',
                )}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <VgsShowValue
                value={log.representativeAddressUnit}
                label='Representative Address Unit'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativeAddressUnit',
                )}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeAddressCity}
                label='Representative Address City'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativeAddressCity',
                )}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeAddressRegion}
                label='Representative Address Region'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativeAddressRegion',
                )}
                {...sharedProps}
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <VgsShowValue
                value={log.representativeAddressPostalCode}
                label='Representative Address Postal Code'
                onRevealSuccess={onFieldRevealSuccess(
                  'representativeAddressPostalCode',
                )}
                {...sharedProps}
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.representativeTitle}
                label='Representative Title'
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.representativeRepresentative}
                label='Representative is Representative'
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.representativeExecutive}
                label='Representative is Executive'
              />
            </div>
            <div className='md:w-1/3 px-3 my-2'>
              <RawDisplayValue
                value={log.representativeDirector}
                label='Representative is Director'
              />
            </div>
          </div>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.representativeOwner}
                label='Representative is Owner'
              />
            </div>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue
                value={log.representativePercentOwnership}
                label='Representative Percent Ownership'
              />
            </div>
          </div>
          <h1 className='text-xl font-medium mb-4'>Stripe Responses</h1>
          <div className='md:flex mb-5'>
            <div className='md:w-1/2 px-3 my-2'>
              <RawDisplayValue value={log.accountId} label='Account ID' />
            </div>
          </div>
          <br />
          <JsonDisplayValue
            label='Create Stripe Connect Account'
            value={log.createStripeConnectAccountResponse}
            {...sharedProps}
          />
          <hr />
          <JsonDisplayValue
            value={log.createStripeRepresentativeResponse}
            label='Create Stripe Representative'
            {...sharedProps}
          />
          <hr />
          <JsonDisplayValue
            value={log.ownersProvidedResponse}
            label='Owners Provided'
            {...sharedProps}
          />
          <hr />
          <JsonDisplayValue
            value={log.createStripeConnectAccountPayableOrgResponse}
            label='Create Stripe Connect Account Payable Org'
            {...sharedProps}
          />
          <hr />
          <JsonDisplayValue
            value={JSON.stringify(
              log.createStripeRepresentativePayableOrgResponse,
            )}
            label='Create Stripe Representative Payable Org'
            {...sharedProps}
          />
          <hr />
          <JsonDisplayValue
            value={JSON.stringify(log.ownersProvidedPayableOrgResponse)}
            label='Owners Provided Payable Org'
            {...sharedProps}
          />
        </div>
      )}
    </Fragment>
  );
}
